/* ==========================================================================
   Signet Media Controller (Scoped per product section)
   - Defaults: picks first Letter + first Size (if empty)
   - Swaps images by Metal + Letter
   - Injects rich text description from metaobjects
   - Mobile: renders swipe slider with dots (scroll-snap) WITHOUT destroying theme gallery
   - If no metaobject exists: shows ORIGINAL theme gallery again
   - ALSO: writes chosen image + selections into line item properties for cart display
   ========================================================================== */

(function () {
  "use strict";

  function initOneSignetInstance(dataEl) {
    if (!dataEl) return;

    var sectionId = dataEl.getAttribute("data-section-id");
    var productId = dataEl.getAttribute("data-product-id");
    if (!sectionId || !productId) return;

    var rootId = "ProductSection-" + sectionId + "-" + productId;
    var root = document.getElementById(rootId);
    if (!root) return;

    // Parse map
    var map = {};
    try {
      map = JSON.parse(dataEl.textContent || "{}");
    } catch (e) {
      return;
    }

    // --------------------------------------------------
    // Instance state
    // --------------------------------------------------
    var activeKey = null;
    var selectedIndex = 0;
    var lastVariantId = getVariantIdFromUrl();

    function clampIndex(i, len) {
      if (!len || len <= 0) return 0;
      var n = parseInt(i, 10);
      if (!Number.isFinite(n)) n = 0;
      if (n < 0) n = 0;
      if (n > len - 1) n = len - 1;
      return n;
    }

    // --------------------------------------------------
    // Utils
    // --------------------------------------------------
    function isMobile() {
      return window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
    }

    function normalizeMetal(raw) {
      var v = String(raw || "")
        .toLowerCase()
        .trim()
        .replace(/-/g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ");

      if (v.includes("white")) return "white";
      if (v.includes("rose")) return "rose";
      if (v.includes("yellow")) return "yellow";
      if (
        v.includes("silver") ||
        v.includes("sterling") ||
        v.includes(".925") ||
        v.includes("925")
      ) return "silver";

      return null;
    }

    function getVariantIdFromUrl() {
      try {
        var url = new URL(window.location.href);
        return url.searchParams.get("variant") || "";
      } catch (e) {
        return "";
      }
    }

    function getCurrentVariant() {
      var el = root.querySelector("[data-current-variant-json]");
      if (!el) return null;

      var raw = el.value || el.textContent || "";
      try {
        return JSON.parse(raw || "{}");
      } catch (e) {
        return null;
      }
    }

    function textLooksLikeMetal(text) {
      return !!normalizeMetal(text || "");
    }

    function getCheckedRadioMetal() {
      var radios = root.querySelectorAll('input[type="radio"]:checked');

      for (var i = 0; i < radios.length; i++) {
        var val = (radios[i].value || "").trim();
        var metal = normalizeMetal(val);
        if (metal) return metal;
      }

      return "";
    }

    function getSelectMetal() {
      var selects = root.querySelectorAll("select");

      for (var i = 0; i < selects.length; i++) {
        var sel = selects[i];
        var val = (sel.value || "").trim();
        var metal = normalizeMetal(val);
        if (metal) return metal;
      }

      return "";
    }

    function getMetalFromFieldsets() {
      var fieldsets = root.querySelectorAll("fieldset");

      for (var i = 0; i < fieldsets.length; i++) {
        var fs = fieldsets[i];
        var legend = fs.querySelector("legend");
        var legendText = (legend ? legend.textContent : "").toLowerCase().trim();

        // In this store the metal option is called "Type"
        var isCandidateFieldset =
          legendText.indexOf("metal") !== -1 ||
          legendText.indexOf("material") !== -1 ||
          legendText.indexOf("color") !== -1 ||
          legendText.indexOf("colour") !== -1 ||
          legendText.indexOf("type") !== -1;

        if (!isCandidateFieldset) continue;

        var checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) {
          var checkedMetal = normalizeMetal(checked.value || "");
          if (checkedMetal) return checkedMetal;
        }

        var radios = fs.querySelectorAll('input[type="radio"]');
        for (var r = 0; r < radios.length; r++) {
          var radioMetal = normalizeMetal(radios[r].value || "");
          if (radioMetal && radios[r].checked) return radioMetal;
        }

        // fallback: any option text inside this fieldset that is active
        var activeEl =
          fs.querySelector("[aria-checked='true']") ||
          fs.querySelector("[aria-selected='true']") ||
          fs.querySelector("[aria-pressed='true']") ||
          fs.querySelector(".is-active") ||
          fs.querySelector(".active");

        if (activeEl) {
          var activeText =
            activeEl.getAttribute("data-value") ||
            activeEl.getAttribute("value") ||
            activeEl.textContent ||
            "";
          var activeMetal = normalizeMetal(activeText);
          if (activeMetal) return activeMetal;
        }
      }

      return "";
    }

    function getSelectedMetal() {
      // 1) Best source: actual checked/active UI
      var metal = "";

      metal = getMetalFromFieldsets();
      if (metal) return metal;

      metal = getCheckedRadioMetal();
      if (metal) return metal;

      metal = getSelectMetal();
      if (metal) return metal;

      // 2) Fallback: current variant JSON
      var v = getCurrentVariant();
      if (v) {
        var opts = [];

        if (Array.isArray(v.options) && v.options.length) {
          opts = v.options.slice();
        } else {
          if (v.option1) opts.push(v.option1);
          if (v.option2) opts.push(v.option2);
          if (v.option3) opts.push(v.option3);
        }

        for (var i = 0; i < opts.length; i++) {
          var m = normalizeMetal(opts[i]);
          if (m) return m;
        }

        var mt = normalizeMetal(v.title || "");
        if (mt) return mt;
      }

      return null;
    }

    function getSelectedLetter() {
      var sel = root.querySelector("#InitialLetter");
      if (sel && sel.tagName === "SELECT") return (sel.value || "").trim().toUpperCase();
      return "";
    }

    function getSelectedSize() {
      var sel = root.querySelector("#InitialSize");
      if (sel && sel.tagName === "SELECT") return (sel.value || "").trim();
      return "";
    }

    function triggerChange(el) {
      if (!el) return;
      el.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function absolutize(url) {
      if (!url) return "";
      if (url.startsWith("//")) return "https:" + url;
      return url;
    }

    function uniq(arr) {
      var out = [];
      arr.forEach(function (x) {
        if (x && out.indexOf(x) === -1) out.push(x);
      });
      return out;
    }

    // --------------------------------------------------
    // Line item properties helpers
    // --------------------------------------------------
    function setLineItemProperty(selector, value) {
      var input = root.querySelector(selector);
      if (!input) return;
      input.value = value || "";
    }

    function syncLineItemProps(opts) {
      opts = opts || {};
      setLineItemProperty("[data-signet-line-image]", opts.image || "");
      setLineItemProperty("[data-signet-line-metal]", opts.metal || "");
      setLineItemProperty("[data-signet-line-letter]", opts.letter || "");
      setLineItemProperty("[data-signet-line-size]", opts.size || "");
    }

    // --------------------------------------------------
    // Photo root + ORIGINAL gallery reference
    // --------------------------------------------------
    function getPhotosRoot() {
      return (
        root.querySelector('[id^="ProductPhotos-"]') ||
        root.querySelector(".product__photos") ||
        root.querySelector(".product-single__photos")
      );
    }

    var photosRoot = getPhotosRoot();
    if (!photosRoot) return;

    function getThemeGalleryEl() {
      var wrap = photosRoot.querySelector("[data-signet-theme-gallery]");
      if (wrap) return wrap;

      wrap = document.createElement("div");
      wrap.setAttribute("data-signet-theme-gallery", "true");

      while (photosRoot.firstChild) {
        wrap.appendChild(photosRoot.firstChild);
      }

      photosRoot.appendChild(wrap);
      return wrap;
    }

    function hideThemeGallery() {
      var theme = getThemeGalleryEl();
      if (theme) theme.style.display = "none";
    }

    function showThemeGallery() {
      var theme = photosRoot.querySelector("[data-signet-theme-gallery]");
      if (theme) theme.style.display = "";
    }

    function removeOurMobileUI() {
      var wrap = photosRoot.querySelector("[data-signet-mobile-slider]");
      if (wrap) wrap.remove();
    }

    function removeOurDesktopUI() {
      var layout = photosRoot.querySelector("[data-signet-desktop-layout]");
      if (layout) layout.remove();
    }

    // --------------------------------------------------
    // Description
    // --------------------------------------------------
    function ensureDescriptionEl() {
      var share = root.querySelector(".product-block--share, .social-sharing");
      if (!share) return null;

      var el = root.querySelector("[data-signet-description]");
      if (el) return el;

      el = document.createElement("div");
      el.setAttribute("data-signet-description", "true");
      el.className = "rte";
      el.style.marginBottom = "12px";

      share.parentNode.insertBefore(el, share);
      return el;
    }

    function setDescription(html) {
      var el = ensureDescriptionEl();
      if (!el) return;

      var clean = (html || "").trim();
      el.innerHTML = clean;
      el.style.display = clean ? "" : "none";
    }

    function clearDescription() {
      var el = root.querySelector("[data-signet-description]");
      if (el) {
        el.innerHTML = "";
        el.style.display = "none";
      }
    }

    function clearUI() {
      clearDescription();
      removeOurMobileUI();
      removeOurDesktopUI();
      showThemeGallery();

      syncLineItemProps({ image: "", metal: "", letter: "", size: "" });
    }

    // --------------------------------------------------
    // Desktop
    // --------------------------------------------------
    function ensureDesktopLayout() {
      if (!photosRoot) return null;

      var layout = photosRoot.querySelector("[data-signet-desktop-layout]");
      if (layout) return layout;

      var theme = getThemeGalleryEl();

      layout = document.createElement("div");
      layout.setAttribute("data-signet-desktop-layout", "true");
      layout.style.display = "flex";
      layout.style.gap = "12px";

      var thumbs = document.createElement("div");
      thumbs.setAttribute("data-signet-thumbs-col", "true");
      thumbs.style.width = "90px";

      var main = document.createElement("div");
      main.setAttribute("data-signet-main-col", "true");
      main.style.flex = "1";

      layout.appendChild(thumbs);
      layout.appendChild(main);

      if (theme && theme.parentNode) {
        theme.parentNode.insertBefore(layout, theme);
      } else {
        photosRoot.prepend(layout);
      }

      return layout;
    }

    function setDesktopMainImage(url) {
      var layout = ensureDesktopLayout();
      if (!layout || !url) return;

      var main = layout.querySelector("[data-signet-main-col]");
      if (!main) return;

      var img = main.querySelector("img[data-signet-main]");
      if (!img) {
        img = document.createElement("img");
        img.setAttribute("data-signet-main", "true");
        img.alt = root.getAttribute("data-product-title") || "Signet Ring";
        img.loading = "eager";
        img.decoding = "async";
        img.fetchPriority = "high";
        img.style.width = "100%";
        img.style.display = "block";
        main.appendChild(img);
      }

      url = absolutize(url);
      if (img.src !== url) img.src = url;
    }

    function renderDesktopThumbs(urls) {
      var layout = ensureDesktopLayout();
      if (!layout) return;

      var col = layout.querySelector("[data-signet-thumbs-col]");
      if (!col) return;

      col.innerHTML = "";

      urls.forEach(function (url, i) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.style.border = "1px solid rgba(0,0,0,.2)";
        btn.style.borderRadius = "6px";
        btn.style.marginBottom = "6px";
        btn.style.padding = "0";
        btn.style.background = "transparent";
        btn.style.cursor = "pointer";

        var img = document.createElement("img");
        img.src = url;
        img.alt = "Thumbnail";
        img.loading = "lazy";
        img.decoding = "async";
        img.style.width = "100%";
        img.style.display = "block";

        btn.appendChild(img);

        btn.addEventListener("click", function () {
          selectedIndex = clampIndex(i, urls.length);
          setDesktopMainImage(urls[selectedIndex]);

          syncLineItemProps({
            image: urls[selectedIndex] || urls[0] || "",
            metal: getSelectedMetal() || "",
            letter: getSelectedLetter() || "",
            size: getSelectedSize() || ""
          });
        });

        col.appendChild(btn);
      });
    }

    // --------------------------------------------------
    // Mobile
    // --------------------------------------------------
    function ensureMobileSlider() {
      if (!photosRoot) return null;

      var wrap = photosRoot.querySelector("[data-signet-mobile-slider]");
      if (wrap) return wrap;

      var theme = getThemeGalleryEl();

      hideThemeGallery();

      wrap = document.createElement("div");
      wrap.setAttribute("data-signet-mobile-slider", "true");

      var track = document.createElement("div");
      track.setAttribute("data-signet-mobile-track", "true");
      track.style.display = "flex";
      track.style.overflowX = "auto";
      track.style.scrollSnapType = "x mandatory";
      track.style.webkitOverflowScrolling = "touch";
      track.style.scrollBehavior = "smooth";
      track.style.gap = "0";
      track.style.padding = "0";
      track.style.margin = "0";
      track.style.msOverflowStyle = "none";
      track.style.scrollbarWidth = "none";

      var dots = document.createElement("div");
      dots.setAttribute("data-signet-mobile-dots", "true");
      dots.style.display = "flex";
      dots.style.justifyContent = "center";
      dots.style.gap = "8px";
      dots.style.marginTop = "10px";

      wrap.appendChild(track);
      wrap.appendChild(dots);

      if (theme) {
        photosRoot.insertBefore(wrap, theme);
      } else {
        photosRoot.prepend(wrap);
      }

      var styleId = "signet-mobile-slider-css";
      if (!document.getElementById(styleId)) {
        var st = document.createElement("style");
        st.id = styleId;
        st.textContent = `
          [data-signet-mobile-track]::-webkit-scrollbar { display: none; }
          [data-signet-dot] { width: 8px; height: 8px; border-radius: 999px; border: 0; padding: 0; opacity: .35; background: #111; }
          [data-signet-dot][aria-current="true"] { opacity: 1; }

          @media (max-width: 768px) {
            .product__thumbs,
            .product-single__thumbnails,
            .product__thumbs-wrapper,
            [data-product-thumbs],
            [data-product-thumbnails] { display:none !important; }
          }
        `;
        document.head.appendChild(st);
      }

      return wrap;
    }

    function renderMobileSlider(urls, alt) {
      var wrap = ensureMobileSlider();
      if (!wrap) return;

      var track = wrap.querySelector("[data-signet-mobile-track]");
      var dots = wrap.querySelector("[data-signet-mobile-dots]");
      if (!track || !dots) return;

      track.innerHTML = "";
      dots.innerHTML = "";

      selectedIndex = clampIndex(selectedIndex, urls.length);

      urls.forEach(function (url, i) {
        var slide = document.createElement("div");
        slide.setAttribute("data-signet-mobile-slide", "true");
        slide.style.flex = "0 0 100%";
        slide.style.scrollSnapAlign = "start";
        slide.style.position = "relative";

        var img = document.createElement("img");
        img.src = url;
        img.alt = alt || "Signet Ring";
        img.loading = i === 0 ? "eager" : "lazy";
        img.decoding = "async";
        img.style.width = "100%";
        img.style.display = "block";

        slide.appendChild(img);
        track.appendChild(slide);

        var dot = document.createElement("button");
        dot.type = "button";
        dot.setAttribute("data-signet-dot", "true");
        dot.setAttribute("aria-label", "Go to image " + (i + 1));
        dot.setAttribute("aria-current", i === selectedIndex ? "true" : "false");

        dot.addEventListener("click", function () {
          selectedIndex = clampIndex(i, urls.length);
          slide.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
        });

        dots.appendChild(dot);
      });

      function syncDots() {
        var w = track.clientWidth || 1;
        var idx = Math.round(track.scrollLeft / w);
        idx = clampIndex(idx, urls.length);
        selectedIndex = idx;

        var all = dots.querySelectorAll("[data-signet-dot]");
        all.forEach(function (d, i) {
          d.setAttribute("aria-current", i === idx ? "true" : "false");
        });

        syncLineItemProps({
          image: urls[selectedIndex] || urls[0] || "",
          metal: getSelectedMetal() || "",
          letter: getSelectedLetter() || "",
          size: getSelectedSize() || ""
        });
      }

      track.addEventListener("scroll", function () {
        window.requestAnimationFrame(syncDots);
      });

      (function restoreScrollPosition() {
        var target = selectedIndex;

        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            var w = track.clientWidth || 0;
            if (w > 0) {
              track.scrollLeft = target * w;
              syncDots();
              return;
            }

            setTimeout(function () {
              var w2 = track.clientWidth || 1;
              track.scrollLeft = target * w2;
              syncDots();
            }, 50);
          });
        });
      })();
    }

    // --------------------------------------------------
    // Defaults
    // --------------------------------------------------
    function pickFirstValidOption(selectEl) {
      if (!selectEl || !selectEl.options) return "";

      var bestVal = "";
      var bestNum = null;

      for (var i = 0; i < selectEl.options.length; i++) {
        var opt = selectEl.options[i];
        if (!opt || opt.disabled) continue;

        var val = (opt.value || "").trim();
        if (!val) continue;

        var normalized = val.replace(",", ".");
        var num = Number(normalized);

        if (Number.isFinite(num)) {
          if (bestNum === null || num < bestNum) {
            bestNum = num;
            bestVal = val;
          }
          continue;
        }

        if (bestNum === null && !bestVal) bestVal = val;
      }

      return bestVal || "";
    }

    function ensureDefaults() {
      var letterSel = root.querySelector("#InitialLetter");
      if (letterSel && !letterSel.value) {
        var v1 = pickFirstValidOption(letterSel);
        if (v1) {
          letterSel.value = v1;
          triggerChange(letterSel);
        }
      }

      var sizeSel = root.querySelector("#InitialSize");
      if (sizeSel && !sizeSel.value) {
        var v2 = pickFirstValidOption(sizeSel);
        if (v2) {
          sizeSel.value = v2;
          triggerChange(sizeSel);
        }
      }
    }

    // --------------------------------------------------
    // Update
    // --------------------------------------------------
    function update() {
      var letter = getSelectedLetter();
      var metal = getSelectedMetal();
      var size = getSelectedSize();

      var key = (metal && letter) ? (metal + "|" + letter) : null;

      console.log("[SIGNET update]", {
        metal: metal,
        letter: letter,
        size: size,
        key: key,
        mapHasKey: key ? !!map[key] : false,
        variantIdFromUrl: getVariantIdFromUrl()
      });

      if (!letter || !metal) {
        clearUI();
        return;
      }

      if (activeKey !== key) {
        activeKey = key;
        selectedIndex = 0;
      }

      var entry = map[key];

      if (!entry || !entry.angle_1) {
        console.warn("[SIGNET] Missing key in JSON map:", key, {
          availableExamples: Object.keys(map).slice(0, 30)
        });

        clearDescription();
        removeOurMobileUI();
        removeOurDesktopUI();
        showThemeGallery();

        syncLineItemProps({
          image: "",
          metal: metal || "",
          letter: letter || "",
          size: size || ""
        });

        return;
      }

      var urls = uniq(
        [entry.angle_1, entry.angle_2, entry.angle_3, entry.angle_4, entry.angle_5, entry.angle_6]
          .map(absolutize)
      );

      if (!urls.length) {
        clearUI();
        return;
      }

      selectedIndex = clampIndex(selectedIndex, urls.length);

      setDescription(entry.description);

      var alt = root.getAttribute("data-product-title") || "Signet Ring";

      hideThemeGallery();

      syncLineItemProps({
        image: urls[selectedIndex] || urls[0] || "",
        metal: metal || "",
        letter: letter || "",
        size: size || ""
      });

      if (isMobile()) {
        removeOurDesktopUI();
        renderMobileSlider(urls, alt);
      } else {
        removeOurMobileUI();
        setDesktopMainImage(urls[selectedIndex] || urls[0]);
        renderDesktopThumbs(urls);
      }
    }

    // --------------------------------------------------
    // Init listeners
    // --------------------------------------------------
    ensureDefaults();

    setTimeout(function () {
      update();
    }, 0);

    root.addEventListener("change", function (e) {
      if (!e || !e.target) return;

      if (e.target.id === "InitialLetter") {
        setTimeout(update, 0);
        return;
      }

      if (e.target.id === "InitialSize") {
        syncLineItemProps({
          image: (root.querySelector("[data-signet-line-image]") || {}).value || "",
          metal: getSelectedMetal() || "",
          letter: getSelectedLetter() || "",
          size: getSelectedSize() || ""
        });
        return;
      }

      if (e.target.tagName === "SELECT") {
        setTimeout(update, 0);
        return;
      }

      if (e.target.matches && e.target.matches('input[type="radio"]')) {
        setTimeout(update, 0);
        return;
      }

      if (e.target.matches && e.target.matches('input[data-variant-input]')) {
        setTimeout(update, 0);
      }
    });

    root.addEventListener("click", function (e) {
      if (!e || !e.target) return;

      var buttonLike = e.target.closest("label, button, .variant-input, .variant__button");
      if (buttonLike) {
        setTimeout(update, 30);
      }
    });

    document.addEventListener("variant:change", function () {
      setTimeout(update, 30);
    });

    window.addEventListener("popstate", function () {
      setTimeout(update, 30);
    });

    // Detect URL ?variant= changes that happen without popstate
    setInterval(function () {
      var currentVariantId = getVariantIdFromUrl();
      if (currentVariantId !== lastVariantId) {
        lastVariantId = currentVariantId;
        setTimeout(update, 30);
      }
    }, 250);

    window.addEventListener("resize", function () {
      setTimeout(update, 50);
    });
  }

  function initAll() {
    var els = document.querySelectorAll("script[data-signet-json]");
    if (!els || !els.length) return;

    els.forEach(function (el) {
      initOneSignetInstance(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();